import React from 'react';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import UGCLayout from '@/components/layouts/UGCLayout';

const UGCLanding = () => {
  return (
    <UGCLayout className="pb-20">
      {/* Hide any legacy mobile tab bar on UGC marketing page */}
      <style>{`.mobile-tab-bar { display: none !important; }`}</style>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/30 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Icon name="Sparkles" size={20} color="white" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">Earn My Edge</p>
              <p className="text-xs text-amber-200">UGC Creator Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-amber-100 hover:text-white hover:bg-white/10"
              onClick={() => (window.location.href = '/ugc-subscription')}
            >
              View Plans
            </Button>
            <Button 
              size="sm" 
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => (window.location.href = '/ugc-authentication?mode=login')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Copy & CTA */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/40 mb-6">
              <Icon name="Video" size={16} color="#FCD34D" />
              <span className="text-sm font-semibold text-amber-100">The UGC Platform for Real Creators</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                Close Deals Faster
              </span>
              <span className="text-white"> Than Ever</span>
            </h1>
            
            <p className="text-xl text-amber-100/80 mb-8 leading-relaxed max-w-lg">
              Scripts. Shot lists. Outreach. Deal tracking. Invoices. Teleprompter. Everything a creator needs to run their UGC business in one beautiful platform.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button 
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                onClick={() => (window.location.href = '/ugc-subscription')}
              >
                Choose Your Plan
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => (window.location.href = '/ugc-authentication?mode=login')}
              >
                Sign In
              </Button>
            </div>

            {/* Features List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { icon: 'Zap', text: 'Smart Script Generator' },
                { icon: 'Target', text: 'Deal Tracker & Leaderboards' },
                { icon: 'Mail', text: 'Cold Email Outreach' },
                { icon: 'DollarSign', text: 'Invoice & Payments' },
                { icon: 'Video', text: 'Mobile Teleprompter' },
                { icon: 'Users', text: 'Creator Community' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon name={item.icon} size={16} color="#FCD34D" />
                  </div>
                  <span className="text-amber-100">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Dashboard Preview */}
          <div className="hidden lg:block">
            <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-b from-slate-900/50 to-slate-950/50 backdrop-blur-sm p-8 shadow-2xl">
              <div className="bg-slate-800/50 rounded-xl p-6 border border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} color="white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-100/60">This Month Revenue</p>
                    <p className="text-2xl font-bold text-white">$8,750</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Deals Closed', value: '12', icon: 'Handshake' },
                    { label: 'Scripts Created', value: '28', icon: 'FileText' },
                    { label: 'Response Rate', value: '42%', icon: 'Mail' },
                    { label: 'Avg Deal Value', value: '$729', icon: 'DollarSign' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <p className="text-xs text-amber-100/60 mb-1">{stat.label}</p>
                      <p className="text-lg font-bold text-amber-100">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Everything You Need <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">to Succeed</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: 'Layout', title: 'Dashboard', desc: 'Deal lanes, revenue tracking, leaderboards, and quick access to all tools.' },
            { icon: 'FileText', title: 'Smart Scripts', desc: 'AI-powered scripts and shot lists tailored to each platform.' },
            { icon: 'Send', title: 'Outreach Engine', desc: 'Cold email, DM templates, and package management built-in.' },
            { icon: 'Zap', title: 'Deal Tracker', desc: 'Never lose track of deals. Track status, value, and timelines.' },
            { icon: 'Video', title: 'Teleprompter', desc: 'Mobile-ready scrolling with adjustable speed and formatting.' },
            { icon: 'Users', title: 'Creator Community', desc: 'Connect with other UGC creators, share wins, and collaborate.' },
          ].map((item) => (
            <div key={item.title} className="group p-6 rounded-2xl border border-white/10 hover:border-amber-500/50 bg-slate-800/40 hover:bg-slate-800/80 transition-all">
              <div className="w-12 h-12 rounded-lg bg-amber-500/20 group-hover:bg-amber-500/30 flex items-center justify-center mb-4 transition-colors">
                <Icon name={item.icon} size={24} color="#FCD34D" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-amber-100/70">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
          Plans for Every Creator
        </h2>
        <p className="text-amber-100/70 text-center mb-12 max-w-2xl mx-auto">
          Start with Creator and upgrade anytime. Paid membership required for access.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { 
              name: 'Creator', 
              price: '$79', 
              period: '/month',
              desc: 'Perfect for solo creators',
              whopMonthly: 'plan_5BLztZ9jC4KzK',
              whopAnnual: 'plan_noxLXIJbAgqD3',
              features: ['UGC Dashboard', 'Scripts & Shot Lists', 'Outreach Manager', 'Teleprompter', '100 Analyzer credits/month'],
              cta: 'Get Started'
            },
            { 
              name: 'Creator Elite', 
              price: '$129', 
              period: '/month',
              desc: 'Recommended for growth',
              whopMonthly: 'plan_0BaO8IscYTvfS',
              whopAnnual: 'plan_Q9XYlNHreWOJU',
              features: ['Everything in Creator', '500 Analyzer credits/month', 'Priority Processing', 'Advanced Feedback', 'Community Features'],
              cta: 'Try Elite',
              recommended: true
            },
            { 
              name: 'Founder Lifetime', 
              price: '$1,000', 
              period: 'one-time',
              desc: 'Limited to 30 slots',
              whopMonthly: 'plan_oCcNRWEpF7Go8',
              whopAnnual: 'plan_oCcNRWEpF7Go8',
              features: ['All Creator Elite features', 'Lifetime access', 'Founders badge', 'Priority support', 'Early access to new features'],
              cta: 'Claim Your Slot',
              limited: true
            },
          ].map((plan) => (
            <div 
              key={plan.name} 
              className={`rounded-2xl p-8 border transition-all ${
                plan.recommended 
                  ? 'border-amber-500 bg-gradient-to-br from-amber-500/10 to-orange-500/10 ring-2 ring-amber-500/20 transform scale-105' 
                  : 'border-white/10 bg-slate-800/40 hover:bg-slate-800/60'
              }`}
            >
              {plan.recommended && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 mb-4">
                  <Icon name="Star" size={14} color="#FCD34D" />
                  <span className="text-xs font-semibold text-amber-100">Recommended</span>
                </div>
              )}
              {plan.limited && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 mb-4">
                  <Icon name="Clock" size={14} color="#FCD34D" />
                  <span className="text-xs font-semibold text-amber-100">Only 30 Available</span>
                </div>
              )}
              
              <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
              <p className="text-amber-100/70 text-sm mb-4">{plan.desc}</p>
              
              <div className="mb-6">
                <span className="text-4xl font-bold text-white">{plan.price}</span>
                <span className="text-amber-100/70 text-sm">{plan.period}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-amber-100/80">
                    <Icon name="Check" size={16} color="#FCD34D" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                size="lg"
                className={`w-full font-semibold ${
                  plan.recommended
                    ? 'bg-amber-500 hover:bg-amber-600 text-white'
                    : 'border-amber-500/40 text-amber-100 hover:bg-white/10'
                }`}
                onClick={() => (window.location.href = `https://whop.com/checkout/${plan.whopMonthly}?d2c=true`)}
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Footer */}
      <section className="max-w-7xl mx-auto px-4 py-16 text-center">
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-sm p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Close More Deals?
          </h2>
          <p className="text-amber-100/80 mb-8 max-w-2xl mx-auto">
            Join 1000+ UGC creators already using our platform to manage deals and grow their business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold"
              onClick={() => (window.location.href = '/ugc-subscription')}
            >
              View Plans & Pricing
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => (window.location.href = '/ugc-authentication?mode=login')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>
    </UGCLayout>
  );
};

export default UGCLanding;
